/**
 * A message sent from Cobrain, standing in the conversation until Cobrain's own copy of it arrives (B1411).
 *
 * WHY THERE IS A GAP AT ALL. Sending goes straight to Graph, so Teams has the message before the button
 * has finished its click. Cobrain answers from its own store, and the store gets it when the Teams
 * connector's next delta round runs -- tens of seconds. Mike, 2026-09-21: "When I send a message I get
 * Sent to Teams · it appears here when Cobrain next reads this conversation. But this has to be instant?"
 *
 * The old page drew that sentence under every message he sent, and left it there. Two things were wrong
 * with it: it explained Cobrain's reading schedule to somebody who had just sent a message and had no
 * reason to care, and the bubble never went away, so once the real copy arrived he had the same message
 * on screen twice. This is the rule that ends it, kept away from the component so it can be proved.
 */

/** What was sent from this page in this sitting. */
export interface SentMessage { at: string; body: string }
/** As much of a message in the pane as this rule looks at. */
export interface SeenMessage { mine: boolean; body: string | null; sentAt: string }

/**
 * How far before a bubble's own timestamp a stored message may sit and still be counted as that bubble.
 *
 * It is needed because the two clocks are different: `at` is the browser's, `sentAt` is Teams', and a
 * message can be recorded a little before the moment the browser thought it sent it. It is a minute
 * rather than a day because the match is on the TEXT, and a minute is long enough for any clock here to
 * be out by and far too short for "ok" said this morning to be mistaken for "ok" said now.
 */
export const SENT_SLACK_MS = 60_000;

/** Has this sent message come back as one of Cobrain's own? */
export function hasLanded(sent: SentMessage, seen: readonly SeenMessage[]): boolean {
  const said = sent.body.trim();
  if (!said) return false;
  const floor = Date.parse(sent.at) - SENT_SLACK_MS;
  return seen.some((m) => m.mine && (m.body ?? '').trim() === said && Date.parse(m.sentAt) >= floor);
}

/** The ones still to arrive, in the order they were sent. */
export function stillSending(sent: readonly SentMessage[], seen: readonly SeenMessage[]): SentMessage[] {
  return sent.filter((s) => !hasLanded(s, seen));
}
